import { useEffect, useMemo, useRef } from 'react'
import { observer } from 'mobx-react-lite'
import { Actions, toastStore, variantActionsProps } from 'shared/ui'
import { pluralize } from 'shared/lib'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { type IResponseCancelInvite, type IResponseResendInvite } from 'entities/Users/api/types'
import { useEventCancelInvite } from 'entities/Users/events/EventCancelInvite'
import { useEventResendInvite } from 'entities/Users/events/EventResendInvite'
import { useMembersViewContext } from 'pages/settings/pages/members/pages/membersView/context/membersViewContext'
import { MembersTable } from 'pages/settings/pages/members/pages/membersView/ui/MembersTable/MembersTable'
import {
  getPendingMembersActions,
  getPendingMembersBulkActions,
} from 'pages/settings/pages/members/ui/actions'
import { getPendingMembersColumns } from './columns'

const pinnedColumnIds = ['email']

export const PendingMembersTab = observer(() => {
  const entities = useEntitiesContext()
  const { fetchCounts, pendingMembersStore } = useMembersViewContext()
  const { init, tableStore, loading, search, setSearch, itemsRows, scrollToTopTrigger, loadData } =
    pendingMembersStore

  useEffect(() => {
    init()
    return () => setSearch('')
  }, [])

  const columns = useMemo(() => getPendingMembersColumns(), [])

  const responseCancelInviteRef = useRef<IResponseCancelInvite | null>(null)
  const cancelInviteSetRef = useRef<Set<number>>(new Set())

  const responseResendInviteRef = useRef<IResponseResendInvite | null>(null)
  const resendInviteSetRef = useRef<Set<number>>(new Set())

  const onCancelInvites = (response: IResponseCancelInvite) => {
    responseCancelInviteRef.current = response
    cancelInviteSetRef.current.clear()
    toastStore.add({
      type: 'info',
      title: `Canceling ${pluralize(
        response.data.chunks_count,
        'invite',
        'invites'
      )}. This may take a moment.`,
    })
  }

  const onResendInvites = (response: IResponseResendInvite, count: number) => {
    if (response.status === 'resent') {
      fetchCounts()
      loadData()
      toastStore.add({
        type: 'info',
        title: `${pluralize(count, 'Invite', 'Invites')} resent`,
      })
      return
    }

    responseResendInviteRef.current = response
    resendInviteSetRef.current.clear()
    toastStore.add({
      type: 'info',
      title: `Resending ${pluralize(
        response.data.chunks_count,
        'invite',
        'invites'
      )}. This may take a moment.`,
    })
  }

  useEventResendInvite((response) => {
    const currentResponse = responseResendInviteRef.current
    if (currentResponse?.status === 'started' && currentResponse.data.bulk_id === response.bulkId) {
      resendInviteSetRef.current.add(response.index)
      if (currentResponse.data.chunks_count === resendInviteSetRef.current.size) {
        fetchCounts()
        loadData()
        toastStore.add({
          type: 'info',
          title: `${pluralize(resendInviteSetRef.current.size, 'Invite', 'Invites')} resent`,
        })
      }
    }
  })

  useEventCancelInvite((response) => {
    if (responseCancelInviteRef.current?.data.bulk_id === response.bulkId) {
      cancelInviteSetRef.current.add(response.index)
      if (responseCancelInviteRef.current.data.chunks_count === cancelInviteSetRef.current.size) {
        fetchCounts()
        loadData()
        const count = cancelInviteSetRef.current.size
        toastStore.add({
          type: 'success',
          title: `Pending ${pluralize(
            count,
            'invite',
            'invites'
          )} canceled successfully. ${pluralize(
            count,
            'Recipient',
            'Recipients'
          )} will not receive ${pluralize(count, 'notification', 'notifications')}.`,
        })
        entities.subscriptionStore.updateSeats()
      }
    }
  })

  const rows = itemsRows.map((item) => {
    item.setActionsProps({
      actions: getPendingMembersActions(entities, item, {
        onCancelInvites: (response: IResponseCancelInvite) => {
          tableStore.bulkStore.onRowSelect(item, false)
          onCancelInvites(response)
        },
        onResendInvite: (response: IResponseResendInvite) => {
          tableStore.bulkStore.onRowSelect(item, false)
          onResendInvites(response, 1)
        },
      }),
    })

    return item
  })

  return (
    <MembersTable
      setSearch={setSearch}
      search={search}
      emptyStateProps={{
        text: 'No members invited yet',
        noIcon: true,
      }}
      tableProps={{
        ariaLabel: 'PendingMembersTable',
        rows,
        columns,
        loading,
        store: tableStore,
        pinnedColumnIds,
        scrollToTOpTrigger: scrollToTopTrigger,
        withCheckbox: true,
        withSelectAll: true,
        headerLeftContentProps: {
          actionsContent: (
            <Actions
              {...variantActionsProps.headerSmall}
              actions={getPendingMembersBulkActions(entities, tableStore.selectedRows, {
                tableStore,
                search,
                onCancelInvitesSuccess: onCancelInvites,
                onResendInvitesSuccess: (response: IResponseResendInvite, count: number) =>
                  onResendInvites(response, count),
              })}
            />
          ),
        },
      }}
    />
  )
})
